import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Send, Phone, Mail, MapPin, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

import { API } from '../config/api';

const SUBJECTS = [
  { value: 'cours-prive', label: 'Cours privé — Tarif sur demande' },
  { value: 'information', label: 'Demande d\'information' },
  { value: 'autre', label: 'Autre' },
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const defaultSubject = searchParams.get('subject') || 'information';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: SUBJECTS.find(s => s.value === defaultSubject)?.label || 'Demande d\'information',
    message: defaultSubject === 'cours-prive'
      ? 'Bonjour, je souhaiterais obtenir des informations sur les cours privés (tarifs, disponibilités, etc.).'
      : '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success('Message envoyé !', { description: 'Nous vous répondrons dans les plus brefs délais.' });
    } catch (error) {
      toast.error('Erreur', { description: error.response?.data?.detail || 'Impossible d\'envoyer votre message.' });
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#F5EFE5] flex items-center justify-center px-4">
        <Card className="bg-white border-[#E9DDCC] rounded-3xl max-w-md w-full">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#8C9B86]/15 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#8C9B86]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading font-medium text-2xl text-[#493329] mb-3">
              Message envoyé
            </h2>
            <p className="text-[#7A6455] mb-6">
              Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
            </p>
            <Button
              onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', subject: 'Demande d\'information', message: '' }); }}
              variant="outline"
              className="border-[#C99A55] text-[#C99A55] rounded-full"
              data-testid="contact-send-another"
            >
              Envoyer un autre message
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F0E8D9] to-[#F5EFE5] py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-heading font-light text-3xl sm:text-4xl text-[#493329] tracking-tight mb-3">
            Nous contacter
          </h1>
          <p className="text-[#7A6455]">
            Une question, une demande de cours privé ? Nous sommes à votre écoute.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-1 space-y-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#493329]">Studio</p>
                <p className="text-sm text-[#7A6455]">PURO Pilates Reformer</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#493329]">Email</p>
                <p className="text-sm text-[#7A6455]">puropilatesreformer@gmail.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#493329]">Téléphone</p>
                <p className="text-sm text-[#7A6455]">Sur rendez-vous</p>
              </div>
            </div>

            {/* Social Media */}
            <div className="pt-2">
              <p className="text-sm font-medium text-[#493329] mb-3">Suivez-nous</p>
              <div className="flex items-center gap-3">
                <a href="https://www.instagram.com/puropilatesreformer" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center text-[#C99A55] hover:bg-[#C99A55] hover:text-white transition-all"
                  data-testid="contact-social-instagram" aria-label="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://www.facebook.com/puropilatesreformer" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center text-[#C99A55] hover:bg-[#C99A55] hover:text-white transition-all"
                  data-testid="contact-social-facebook" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://www.tiktok.com/@puropilatesstudio" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#F0E8D9] flex items-center justify-center text-[#C99A55] hover:bg-[#C99A55] hover:text-white transition-all"
                  data-testid="contact-social-tiktok" aria-label="TikTok">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13a8.28 8.28 0 005.58 2.17V11.7a4.83 4.83 0 01-3.77-1.78V6.69h3.77z"/></svg>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="md:col-span-2 bg-white border-[#E9DDCC] rounded-3xl">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm text-[#493329] mb-1.5 block">
                      Nom complet *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl h-11 focus:ring-[#C99A55]"
                      data-testid="contact-name-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm text-[#493329] mb-1.5 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl h-11 focus:ring-[#C99A55]"
                      data-testid="contact-email-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm text-[#493329] mb-1.5 block">
                    Téléphone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="bg-[#F5EFE5] border-[#E9DDCC] rounded-xl h-11 focus:ring-[#C99A55]"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="subject" className="text-sm text-[#493329] mb-1.5 block">
                    Sujet
                  </Label>
                  <select
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="w-full bg-[#F5EFE5] border border-[#E9DDCC] rounded-xl h-11 px-3 text-sm text-[#493329] focus:outline-none focus:ring-2 focus:ring-[#C99A55]"
                    data-testid="contact-subject-select"
                  >
                    {SUBJECTS.map(s => (
                      <option key={s.value} value={s.label}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm text-[#493329] mb-1.5 block">
                    Message *
                  </Label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Décrivez votre demande..."
                    className="w-full bg-[#F5EFE5] border border-[#E9DDCC] rounded-xl p-3 text-sm text-[#493329] resize-none focus:outline-none focus:ring-2 focus:ring-[#C99A55]"
                    data-testid="contact-message-textarea"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#C99A55] hover:bg-[#B08A4F] text-white rounded-xl h-12"
                  data-testid="contact-submit-button"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Envoyer
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
